import React, { useState } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import Modal from './Modal'
import { formatDate } from '../utils/dateFormat'

interface UpdateProposalDeadlineProps {
  proposalId: number
  currentDeadline: string
  onClose: () => void
  onSuccess: () => void
}

export default function UpdateProposalDeadline({ proposalId, currentDeadline, onClose, onSuccess }: UpdateProposalDeadlineProps) {
  const [deadline, setDeadline] = useState(currentDeadline)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    if (!deadline) {
      setError('Please select a deadline')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      const response = await fetchWithCsrf(`/api/v1/book_proposals/${proposalId}/update_deadline`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voting_deadline: deadline })
      })

      if (response.ok) {
        onSuccess()
        onClose()
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to update deadline')
      }
    } catch (err) {
      setError('Failed to update deadline')
    } finally {
      setSubmitting(false)
    }
  }

  // Get tomorrow's date as minimum
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  const minDate = tomorrow.toISOString().split('T')[0]

  return (
    <Modal isOpen={true} onClose={onClose} size="md" showCloseButton={true}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Update Voting Deadline</h2>
            <p className="text-gray-600 text-sm mt-1">Extend or shorten the voting period</p>
          </div>
        </div>
      </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              New Voting Deadline
            </label>
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              min={minDate}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-2">
              Current deadline: {formatDate(currentDeadline)}
            </p>
          </div>

          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="text-sm text-amber-900">
                <p className="font-semibold mb-1">Note:</p>
                <p className="text-amber-800">
                  All members will see the updated deadline. Consider notifying your club about the change.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all border-2 border-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !deadline}
              className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
            >
              {submitting ? 'Updating...' : 'Update Deadline'}
            </button>
          </div>
        </div>
      </Modal>
  )
}
